/**
 * Sync engine — reconcile the device with the server
 *
 * Pull is cursor-based and server-authoritative: projects, chapters and text
 * are read-only on the client, so applying a change is an overwrite and there
 * is no merge to get wrong. Push is the existing playback queue, which already
 * implements durable last-write-wins (spec 006) and is drained through a hook
 * rather than reimplemented here.
 *
 * The cursor is committed after every page, which is what makes an interrupted
 * run resumable rather than restartable (FR-013).
 *
 * Contract: specs/013-offline-first-pwa/contracts/sync-api.md
 */

import axios from 'axios';
import { getApiUrl } from '../../config';
import { getAccessToken } from '../auth';
import * as repository from './repository';
import { hydrateSkew, recordSkew, nowIso } from './clockSkew';

const CURSOR_KEY = 'syncCursor';
const LAST_SYNCED_KEY = 'lastSyncedAt';
const SKEW_KEY = 'clockSkewMs';
const MAX_PAGES = 500; // runaway guard; a real account never approaches this

let inFlight = null;
let drainPendingWrites = null;
let onSessionRejected = null;

/** Latest outcome, shaped so a telemetry pipeline can consume it later (FR-036). */
let lastOutcome = { status: 'idle', at: null, error: null, pages: 0, changes: 0 };
const listeners = new Set();

const notify = (fn) => {
  try {
    fn(lastOutcome);
  } catch (e) {
    // A broken subscriber must never take the sync engine down with it.
    console.warn('syncEngine: listener failed', e);
  }
};

const emit = (patch) => {
  lastOutcome = { ...lastOutcome, ...patch };
  for (const fn of listeners) notify(fn);
};

export const subscribe = (fn) => {
  listeners.add(fn);
  notify(fn); // deliver the current state immediately, guarded like every other call
  return () => listeners.delete(fn);
};

export const getLastOutcome = () => lastOutcome;

/** Register the pending-write drain (the existing playback queue). */
export const registerPushDrain = (fn) => {
  drainPendingWrites = typeof fn === 'function' ? fn : null;
};

/** Register what to do when the server rejects the session (FR-027). */
export const registerSessionRejectedHandler = (fn) => {
  onSessionRejected = typeof fn === 'function' ? fn : null;
};

/** Restore the persisted clock offset so offline writes are stamped correctly. */
export const hydrate = async () => {
  try {
    hydrateSkew(await repository.getMeta(SKEW_KEY));
  } catch (e) {
    console.warn('syncEngine: could not hydrate skew', e);
  }
};

const fetchPage = async (cursor) => {
  const token = getAccessToken();
  return axios.get(getApiUrl('sync/changes'), {
    params: cursor ? { since: cursor } : {},
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

const statusFor = (error) => {
  if (!error?.response) return 'offline'; // network unreachable is a resting state, not a failure
  return 'error';
};

/** Push local writes. A failed push must never abort the pull. */
async function drainSafely() {
  if (!drainPendingWrites) return;
  try {
    await drainPendingWrites();
  } catch (e) {
    // Stale local writes are better than a device that never learns about
    // server-side changes.
    console.warn('syncEngine: push drain failed', e);
  }
}

/** Fetch and apply one page. Returns the next cursor and whether more remain. */
async function applyPage(cursor) {
  const { data, headers } = await fetchPage(cursor);
  await repository.applyChanges(data?.changes);

  const nextCursor = data?.next_cursor ?? cursor;
  await repository.setMeta(CURSOR_KEY, nextCursor);

  const skew = recordSkew(data?.server_time || headers?.date);
  if (skew !== null) await repository.setMeta(SKEW_KEY, skew);

  return { cursor: nextCursor, hasMore: !!data?.has_more, count: data?.changes?.length || 0 };
}

async function handleFailure(error, pages, changes) {
  const status = error?.response?.status;

  // Session revoked or invalid: stop syncing and clear the account (FR-027).
  if (status === 401 || status === 403) {
    emit({ status: 'signed-out', error: 'session-rejected', pages, changes });
    if (onSessionRejected) await onSessionRejected();
    return lastOutcome;
  }

  // The sync endpoint is not deployed yet. Offline reads come from the
  // write-through cache and are unaffected, so this is not a failure worth
  // reporting to the user or retrying loudly.
  if (status === 404) {
    emit({ status: 'unavailable', error: null, pages, changes });
    return lastOutcome;
  }

  // Cursor too old to serve: rebuild the document view from zero. Downloads and
  // cached audio are deliberately kept — re-downloading gigabytes is not free.
  if (status === 410) {
    await repository.clearDocuments();
    await repository.setMeta(CURSOR_KEY, null);
    emit({ status: 'error', error: 'cursor-expired', pages, changes });
    return lastOutcome;
  }

  // Transient (5xx) or offline: keep the cursor, retry on the next trigger.
  emit({ status: statusFor(error), error: error?.message || 'sync-failed', pages, changes });
  return lastOutcome;
}

async function run() {
  emit({ status: 'syncing', error: null });
  await drainSafely();

  let pages = 0;
  let changes = 0;

  try {
    // Inside the try because it reads the on-device store, which can fail
    // outright when storage is unavailable. Outside it, that rejection escaped
    // `run` altogether — and every trigger calls `sync()` without awaiting, so
    // it surfaced as an unhandled rejection instead of a sync status.
    let cursor = await repository.getMeta(CURSOR_KEY);
    while (pages < MAX_PAGES) {
      const page = await applyPage(cursor);
      cursor = page.cursor;
      changes += page.count;
      pages += 1;
      if (!page.hasMore) break;
    }

    const at = nowIso();
    await repository.setMeta(LAST_SYNCED_KEY, at);
    emit({ status: 'idle', at, error: null, pages, changes });
    return lastOutcome;
  } catch (error) {
    return handleFailure(error, pages, changes);
  }
}

/**
 * Reconcile with the server. Concurrent callers join the run already in
 * flight rather than starting a second one (FR-011).
 */
export const sync = () => {
  if (!inFlight) {
    inFlight = run()
      // Triggers call this without awaiting, so a rejection has nowhere to go.
      // `run` handles its own failures; this is the backstop for one escaping
      // anyway — `handleFailure` itself touches the store it is reporting on.
      .catch((e) => {
        console.warn('syncEngine: sync failed', e);
        return lastOutcome;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

export const isSyncing = () => inFlight !== null;
